import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useAuthStore } from '../store';
import { useOrgStore } from '../store/org';
import { vpApi, errorMessage } from '../api/velopulse';

export default function AccountMenu() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { orgs, currentOrg, clearOrg } = useOrgStore();
  const [open, setOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const wrapRef = useRef(null);
  const itemRefs = useRef([]);
  const multiOrg = Array.isArray(orgs) && orgs.length > 1;

  useEffect(() => {
    let cancelled = false;
    vpApi.getPlatformMe()
      .then((data) => { if (!cancelled) setIsPlatformAdmin(Boolean(data?.is_platform_admin)); })
      .catch(() => { if (!cancelled) setIsPlatformAdmin(false); });
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    const first = itemRefs.current.find(Boolean);
    if (first) first.focus();
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  const openChangePassword = () => {
    setOpen(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPwdOpen(true);
  };

  const submitChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword) { toast.error('請輸入目前密碼'); return; }
    if (newPassword.length < 8) { toast.error('新密碼至少 8 個字元'); return; }
    if (newPassword !== confirmPassword) { toast.error('兩次新密碼不一致'); return; }
    if (newPassword === currentPassword) { toast.error('新密碼不可與目前密碼相同'); return; }
    setSaving(true);
    try {
      await axios.post('/api/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('密碼已更新');
      setPwdOpen(false);
    } catch (err) {
      toast.error(errorMessage(err, '無法更新密碼'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    setOpen(false);
    clearOrg();
    logout();
    navigate('/login');
  };

  const focusItem = (index) => {
    const items = itemRefs.current.filter(Boolean);
    if (!items.length) return;
    const next = (index + items.length) % items.length;
    items[next].focus();
  };

  const onMenuKey = (e) => {
    const items = itemRefs.current.filter(Boolean);
    const idx = items.indexOf(e.currentTarget);
    if (e.key === 'ArrowDown') { e.preventDefault(); focusItem(idx + 1); }
    if (e.key === 'ArrowUp') { e.preventDefault(); focusItem(idx - 1); }
    if (e.key === 'Home') { e.preventDefault(); focusItem(0); }
    if (e.key === 'End') { e.preventDefault(); focusItem(items.length - 1); }
  };

  const setItemRef = (i) => (el) => { itemRefs.current[i] = el; };

  let idx = 0;
  const nextIdx = () => { const i = idx; idx += 1; return i; };

  return (
    <>
      <div className="vp-account" ref={wrapRef}>
        <button
          type="button"
          className="vp-avatar"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls="vp-account-menu"
          aria-label={`帳號選單：${user?.name || '使用者'}`}
          title={user?.name || '帳號'}
          onClick={() => setOpen((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setOpen(true);
            }
          }}
        >
          {(user?.name || '我').slice(0, 1)}
        </button>
        {open && (
          <div className="vp-account-menu" role="menu" id="vp-account-menu">
            <div className="vp-account-who">
              <strong>{user?.name || '使用者'}</strong>
              {user?.email ? <div className="vp-muted">{user.email}</div> : null}
              {currentOrg ? <div className="vp-muted">目前公司：{currentOrg.name}</div> : null}
              {isPlatformAdmin ? <div className="vp-muted">身分：平台管理員</div> : null}
            </div>
            <button type="button" role="menuitem" ref={setItemRef(nextIdx())} onKeyDown={onMenuKey} onClick={() => go('/settings')}>我的帳號／設定</button>
            <button type="button" role="menuitem" ref={setItemRef(nextIdx())} onKeyDown={onMenuKey} onClick={openChangePassword}>修改密碼</button>
            {isPlatformAdmin && (
              <button type="button" role="menuitem" ref={setItemRef(nextIdx())} onKeyDown={onMenuKey} onClick={() => go('/platform')}>平台管理</button>
            )}
            {multiOrg && (
              <button type="button" role="menuitem" ref={setItemRef(nextIdx())} onKeyDown={onMenuKey} onClick={() => go('/orgs')}>切換公司</button>
            )}
            <button type="button" role="menuitem" ref={setItemRef(nextIdx())} onKeyDown={onMenuKey} onClick={handleLogout}>登出</button>
          </div>
        )}
      </div>

      {pwdOpen && (
        <div className="vp-modal-mask" onClick={(e) => { if (e.target === e.currentTarget && !saving) setPwdOpen(false); }}>
          <div className="vp-modal" role="dialog" aria-labelledby="change-password-title" style={{ maxWidth: 420 }}>
            <div className="vp-modal-head">
              <h2 id="change-password-title">修改密碼</h2>
              <button type="button" className="vp-modal-x" onClick={() => !saving && setPwdOpen(false)} aria-label="關閉">✕</button>
            </div>
            <form onSubmit={submitChangePassword}>
              <label className="vp-label">目前密碼
                <input className="vp-input" type="password" autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
              </label>
              <label className="vp-label">新密碼（至少 8 字元）
                <input className="vp-input" type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} />
              </label>
              <label className="vp-label">再輸入一次新密碼
                <input className="vp-input" type="password" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} />
              </label>
              <div className="vp-wiz-actions">
                <button type="button" className="vp-btn" disabled={saving} onClick={() => setPwdOpen(false)}>取消</button>
                <button type="submit" className="vp-btn primary" disabled={saving}>{saving ? '更新中…' : '確認更新'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
